export const FLOATING_STACK_MAX_VISIBLE = 3

export interface IFloatingStackLayoutEntry {
  // unique across server popups and local messages, e.g. `server:<id>` / `local:<id>`
  key: string
  // flow order: oldest first; a ×N bump or a replace moves a local card to "now"
  queuedAt: number
  seq: number
  isPinned: boolean
  // 1 persistent → 2 progress → 3 connection; only read for pinned entries
  pinRank: number
  severity: string
  isHovered: boolean
  isFocused: boolean
  isTouched: boolean
  // a card in its leave animation keeps its place and its collapsed state, but is no longer counted
  isLeaving?: boolean
  isCollapsed?: boolean
}

export interface IFloatingStackLayoutOptions {
  // --msg-max-visible read from the card look (mobile bottom sheet: 1); ignored when not a positive number
  cssMaxVisible?: number | null
  // the pill's "Show" button: expand everything until the next layout
  showAllOnce?: boolean
}

export interface IFloatingStackLayout<T extends IFloatingStackLayoutEntry> {
  // DOM order after the pill: flow oldest → newest, then pinned by rank
  ordered: T[]
  collapsedKeys: string[]
  overflowCount: number
  // severities of the collapsed cards, oldest first (data-overflow-severities on the pill)
  overflowSeverities: string[]
  visibleCount: number
  pinnedCount: number
  maxVisible: number
}

function compareByQueue(first: IFloatingStackLayoutEntry, second: IFloatingStackLayoutEntry): number {
  return first.queuedAt - second.queuedAt
    || first.seq - second.seq
    || first.key.localeCompare(second.key)
}

function comparePinned(first: IFloatingStackLayoutEntry, second: IFloatingStackLayoutEntry): number {
  return first.pinRank - second.pinRank || compareByQueue(first, second)
}

// One stack for server popups and local messages: at most maxVisible flow cards, the oldest ones collapse
// into the "+N" pill, but never a card that is hovered, focused or touched (the next one collapses instead)
export function computeFloatingStackLayout<T extends IFloatingStackLayoutEntry>(
  entries: T[],
  options: IFloatingStackLayoutOptions = {},
): IFloatingStackLayout<T> {
  const cssMaxVisible = options.cssMaxVisible
  const maxVisible = cssMaxVisible != null && Number.isFinite(cssMaxVisible) && cssMaxVisible > 0
    ? Math.min(FLOATING_STACK_MAX_VISIBLE, Math.floor(cssMaxVisible))
    : FLOATING_STACK_MAX_VISIBLE

  const flow = entries.filter(entry => !entry.isPinned).sort(compareByQueue)
  const pinned = entries.filter(entry => entry.isPinned).sort(comparePinned)
  const liveFlow = flow.filter(entry => !entry.isLeaving)

  const collapsedKeys = new Set<string>()
  let toCollapse = options.showAllOnce ? 0 : Math.max(0, liveFlow.length - maxVisible)
  for(const entry of liveFlow) {
    if(toCollapse <= 0) {
      break
    }
    if(entry.isFocused || entry.isHovered || entry.isTouched) {
      continue
    }
    collapsedKeys.add(entry.key)
    toCollapse--
  }

  const overflowEntries = liveFlow.filter(entry => collapsedKeys.has(entry.key))
  entries.forEach(entry => {
    if(entry.isLeaving && entry.isCollapsed) {
      collapsedKeys.add(entry.key)
    }
  })

  return {
    ordered: [...flow, ...pinned],
    collapsedKeys: [...collapsedKeys],
    overflowCount: overflowEntries.length,
    overflowSeverities: overflowEntries.map(entry => entry.severity),
    visibleCount: liveFlow.length - overflowEntries.length,
    pinnedCount: pinned.filter(entry => !entry.isLeaving).length,
    maxVisible,
  }
}
